import {
  GetTaskByIdDocument,
  useUpdateDueDateTaskAndSubtasksMutation,
} from '@exam-management/graphql';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormDateInput,
  type DialogProps,
} from '@shared/components';
import { useLogger } from '@shared/logger';
import { useSnackbar } from '@shared/snackbar';
import { useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';

import { useQSetReviewTask } from '../../providers';

interface EditTaskDueDateFormValues {
  dueDate: Date | null;
}

export interface EditTaskDueDateModalProps extends Omit<DialogProps, 'onSubmit' | 'open'> {
  onClose: () => void;
  open?: boolean;
}

export default function EditTaskDueDateModal(props: Readonly<EditTaskDueDateModalProps>) {
  const { onClose, open = false } = props;

  const { mainTaskDetail } = useQSetReviewTask();
  const { showSnackbar } = useSnackbar();
  const logger = useLogger();

  const [updateDueDateTaskAndSubtasks, { loading: isSubmitting }] =
    useUpdateDueDateTaskAndSubtasksMutation({
      onCompleted: () => {
        showSnackbar({ message: 'Update Due Date Successfully', variant: 'success' });
        methods.reset();
        onClose();
      },
      onError: (error) => {
        logger.error(error);
        showSnackbar({ message: 'Update Due Date Unsuccessfully', variant: 'error' });
      },
      refetchQueries: [
        {
          query: GetTaskByIdDocument,
          variables: {
            getTaskByIdInput: {
              taskId: mainTaskDetail.id,
            },
          },
        },
      ],
    });

  const methods = useForm<EditTaskDueDateFormValues>({
    defaultValues: {
      dueDate: mainTaskDetail.dueDate ?? null,
    },
  });

  useEffect(() => {
    if (open) {
      methods.reset({
        dueDate: mainTaskDetail.dueDate ?? null,
      });
    }
  }, [open, mainTaskDetail.dueDate, methods]);

  const handleClose = () => {
    methods.reset();
    onClose();
  };

  const handleSubmit = methods.handleSubmit(async (data) => {
    if (!data.dueDate) return;

    await updateDueDateTaskAndSubtasks({
      variables: {
        updateDueDateTaskAndSubtasksInput: {
          id: mainTaskDetail.id,
          dueDate: data.dueDate,
        },
      },
    });
  });

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      PaperProps={{ sx: { overflow: 'visible' } }}
    >
      <DialogTitle onClose={handleClose}>Edit Due Date</DialogTitle>
      <Divider />
      <DialogContent sx={{ overflow: 'visible' }}>
        <FormProvider {...methods}>
          <FormDateInput
            name='dueDate'
            label='Due Date'
            required
            inputFormat='DD-MM-YYYY'
            rules={{ required: 'Due Date is required' }}
            dropdownPosition='bottom-start'
          />
        </FormProvider>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button variant='outlined' color='secondary' onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button variant='contained' onClick={handleSubmit} loading={isSubmitting}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
